import { useStore } from '@stores/Portal/store.js'
import { fetchJson } from '../../../fetch.js'
import { actions as filterActions } from '../../../mixins/store/filters.js'

// Scope filter memory to the current URL so pages don't share local storage
const urlScopedKey = (key) => {
    const current = typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : 'default';

    return `${current}::${key}`;
};

// Helper to read from localStorage with current URL scope
const readLocalStorage = (key, fallback = null) => {
    if (typeof localStorage === 'undefined') {
        return fallback;
    }
    
    const scopedKey = urlScopedKey(key);
    const value = localStorage.getItem(scopedKey);

    if (key === 'product_page_filter_memory_misc_filters') {
        const base = { favourites: false }
        if (value === null || value === '') {
            return { ...base }
        }
        try {
            const parsed = JSON.parse(value)
            if (typeof parsed === 'object' && parsed !== null && ! Array.isArray(parsed)) {
                return { ...base, ...parsed }
            }
        } catch (e) {
            // ignore
        }
        return { ...base }
    }
    
    if (value === null) {
        return fallback;
    }
    
    // Try to parse as JSON for array values (brands, certifications)
    if (key.includes('brands') || key.includes('certifications')) {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : fallback;
        } catch (e) {
            // If it's a string that looks like an array, try to handle it
            if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
                try {
                    const parsed = JSON.parse(value);
                    return Array.isArray(parsed) ? parsed : fallback;
                } catch (e2) {
                    return fallback;
                }
            }
            return fallback;
        }
    }
    
    return value === '' ? null : value;
};

export default {
    doClearFilters() {
        return filterActions.doClearFilters.call(this).then(() => {
            this.filterState.miscFilters.favourites = false
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(
                    urlScopedKey('product_page_filter_memory_misc_filters'),
                    JSON.stringify(this.filterState.miscFilters)
                )
            }
        })
    },

    async doLoadPage(displayLoad = true) {
        if (! this.isLoading && this.pageIsReady) {
            this.loadPageRequest(displayLoad)

            let page = this.pagination.current_page
            let checkedFilters = this.checkedFilters
            let search = this.filterState.search
            let category = this.filterState.category

            let favourites = this.filterState.miscFilters.favourites

            if (! search) search = ""

            let url = `/api/products/${category.id}?` + new URLSearchParams({
                search,
                width: window.innerWidth,
                page,
                ...checkedFilters,
                favourites
            })

            fetchJson({ url, method: 'GET' })
                .then(response => {
                    if (response.ok) {
                        response.json()
                            .then(json => {
                                this.loadPageRequestSuccess(json)
                            })
                    }
                }).catch(err => {
                    this.loadPageRequestFailure()
                })
        }
    },

    async loadFilters() {
        // Refresh memory values from localStorage with current URL scope
        // This is important for SPAs where the store persists but we need fresh values
        const currentSearch = readLocalStorage('product_page_filter_memory_search', null);
        const currentBrands = readLocalStorage('product_page_filter_memory_brands', []);
        const currentCerts = readLocalStorage('product_page_filter_memory_certifications', []);
        const currentMiscFilters = readLocalStorage('product_page_filter_memory_misc_filters', null);
        
        // Update the store state with fresh values from localStorage
        this.filterState.search = currentSearch;
        this.filterState.memory.brands = currentBrands;
        this.filterState.memory.certifications = currentCerts;
        this.filterState.miscFilters = currentMiscFilters;
        
        let category = this.filterState.category
        let url = `/api/filters/product/${category.id}`

        let urlParams = new URLSearchParams(
            window.location.search
        );

        if (urlParams.has("init_brand")) {
            url += '?brands=' + urlParams.get('init_brand')
        }

        if (urlParams.has("init_certification")) {
            let operator = urlParams.has("init_brand")
                ? "?"
                : "&"

            url += `${operator}certifications=` + urlParams.get('init_certification')
        }

        const response = await fetchJson({
            url,
            method: 'GET'
        })

        if (response.ok) {
            response.json().then(json => {
                this.loadFiltersRequestSuccess(json)

                let urlParams = new URLSearchParams(window.location.search)
                let brands = urlParams.get('init_brand');
                let certifications = urlParams.get('init_certification');

                if (brands) {
                    this.initBrand(brands.split(','))
                    urlParams.delete('init_brand')
                } else {
                    // Check if memory.brands is an array with items
                    const memoryBrands = this.filterState.memory.brands;
                    if (Array.isArray(memoryBrands) && memoryBrands.length > 0) {
                        this.initBrand(memoryBrands)
                    }
                }

                if (certifications) {
                    this.initCertification(certifications.split(','))
                    urlParams.delete('init_certification')
                } else {
                    // Check if memory.certifications is an array with items
                    const memoryCerts = this.filterState.memory.certifications;
                    if (Array.isArray(memoryCerts) && memoryCerts.length > 0) {
                        this.initCertification(memoryCerts)
                    }
                }

                this.doSetPageIsReady()
                    .then(() => this.doLoadPage())
            })
        } else {
            this.loadFiltersRequestFailure()
        }
    },

    async doLoadMore() {
        if (this.pageIsReady) {
            this.loadMoreRequest()

            let checkedFilters = this.checkedFilters
            let search = this.filterState.search
            let category = this.filterState.category
            if (! search) search = ""

            let page = this.pagination.current_page

            let favourites = this.filterState.miscFilters.favourites

            let url = `/api/products/${category.id}?` + new URLSearchParams({
                search,
                width: window.innerWidth,
                page,
                ...checkedFilters,
                favourites
            })

            fetchJson({ url, method: 'GET' })
                .then(response => {
                    if (response.ok) {
                        response.json()
                            .then(json => {
                                this.loadMoreRequestSuccess(json)
                                if (json.meta && typeof json.meta === 'object') {
                                    this.meta = { ...json.meta }
                                }
                            })
                    }
                }).catch(err => {
                    this.loadMoreRequestFailure()
                })
        }
    },

    /**
     * Fetches `meta` for the current category and filters (`meta=true`) and updates only `this.meta`.
     */
    async refreshProductListMeta() {
        if (! this.pageIsReady || ! this.filterState.category?.id) {
            return
        }

        const category = this.filterState.category
        const checkedFilters = this.checkedFilters
        let search = this.filterState.search
        if (! search) search = ''
        const page = this.pagination.current_page
        const favourites = this.filterState.miscFilters.favourites
        const width = typeof window !== 'undefined' ? window.innerWidth : 0

        const url = `/api/products/${category.id}?` + new URLSearchParams({
            search,
            width,
            page,
            ...checkedFilters,
            favourites,
            meta: 'true',
        })

        try {
            const response = await fetchJson({ url, method: 'GET' })
            if (! response.ok) {
                return
            }
            const json = await response.json()
            if (json.meta && typeof json.meta === 'object') {
                this.meta = { ...json.meta }
            }
        } catch (e) {
            // Leave existing meta unchanged on failure
        }
    },

    loadPageRequestSuccess(data) {
        this.data = data.data
        this.meta = data.meta && typeof data.meta === 'object' ? { ...data.meta } : {}
        this.pagination = {
            current_page: data.current_page,
            last_page: data.last_page,
            total: data.total
        }
        this.loading = false
        this.noResultsMessage = null
        this.resultsTotal = data.total
        useStore().setPageIsLoading(false)
        if (! data.data.length) {
            this.noResultsMessage = 'Sorry No Products Match Your search...'
            if (this.filterState.search) {
                this.noResultsMessage = 'Sorry No Products Match Your Search... please try clearing the filter.'
            }
        }

        // Set local storage and update memory state
        const searchKey = urlScopedKey('product_page_filter_memory_search');
        const searchValue = this.filterState.search ?? '';
        localStorage.setItem(searchKey, searchValue);
        this.filterState.search = searchValue === '' ? null : searchValue;
        
        const brandsKey = urlScopedKey('product_page_filter_memory_brands');
        const brandsValue = this.filterState
            .filters
            .brands
            .filter(brand => brand.selected)
            .map(brand => brand.id);
        localStorage.setItem(brandsKey, JSON.stringify(brandsValue));
        this.filterState.memory.brands = brandsValue; // Keep memory state in sync
        
        const certsKey = urlScopedKey('product_page_filter_memory_certifications');
        const certsValue = this.filterState
            .filters
            .certifications
            .filter(certification => certification.selected)
            .map(certification => certification.id);
        localStorage.setItem(certsKey, JSON.stringify(certsValue));
        this.filterState.memory.certifications = certsValue; // Keep memory state in sync

        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(
                urlScopedKey('product_page_filter_memory_misc_filters'),
                JSON.stringify(this.filterState.miscFilters)
            )
        }
    },

    initBrand (brandIds) {
        const brandIdStrings = brandIds.map(id => String(id));
        
        this.filterState.filters.brands.forEach(brand => {
            if (brandIdStrings.includes(String(brand.id))) {
                brand.selected = true
            }
        })
        
        // Update memory state to keep it in sync
        this.filterState.memory.brands = brandIds;
        
        // Store as JSON string for proper parsing later
        const storageKey = urlScopedKey('product_page_filter_memory_brands');
        const valueToStore = JSON.stringify(brandIds);
        localStorage.setItem(storageKey, valueToStore);
    },

    initCertification (certificationIds) {
        
        const certIdStrings = certificationIds.map(id => String(id));
        
        this.filterState.filters.certifications.forEach(certification => {
            if (certIdStrings.includes(String(certification.id))) {
                certification.selected = true
            }
        })

        // Update memory state to keep it in sync
        this.filterState.memory.certifications = certificationIds;
        
        // Store as JSON string for proper parsing later
        const storageKey = urlScopedKey('product_page_filter_memory_certifications');
        const valueToStore = JSON.stringify(certificationIds);
        localStorage.setItem(storageKey, valueToStore);
    },

    setFilterCategory(category) {
        this.filterState.category = category
    },

    toggleFavourites() {
        this.filterState.miscFilters.favourites = ! this.filterState.miscFilters.favourites

        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(
                urlScopedKey('product_page_filter_memory_misc_filters'),
                JSON.stringify(this.filterState.miscFilters)
            )
        }

        this.doLoadPage()
    }
}
